import React from 'react';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";
import FreeHighlight from '../partials/FreeHighlight';

const NamedOrderPlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Named order - Aronium</title>
          <meta name="description" content="Assign customer name to an open order" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            Named order
          </h1>
          <p>
            Assign specific names to open orders, facilitating efficient customer tracking and order management.
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            {/* Download for Aronium Lite Button */}
            <a
              href={`${BASE_URL}/plugins/named-order/latest/Aronium.Pos.NamedOrder.Lite.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-rose-500 hover:bg-rose-600 transition duration-300 shadow-lg focus:ring-4 focus:ring-rose-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Lite
            </a>

            {/* Download for Aronium Pro Button */}
            <a
              href={`${BASE_URL}/plugins/named-order/latest/Aronium.Pos.NamedOrder.Pro.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-white bg-[#4A90E2] hover:bg-[#3B7FBF] transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-200 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Download for Aronium Pro
            </a>
          </div>
        </div>
      </section>

      <div className="flex flex-col text-center mt-10">
        <div className="grid place-content-center justify-items-center px-4">
          <img src="/images/order-name.png" className='rounded-t-lg xl:max-w-6xl' alt="Print stations configuration" />
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use <span className='font-semibold'>Named order</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 mx-auto">
            The <span className='font-semibold'>Named order</span> plugin enables businesses to assign unique names to each open order. This feature is particularly beneficial for tracking in-store customers by name, ensuring accurate order processing and enhancing customer service.
          </p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features Section */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Custom order naming</h3>
            <p className="mt-4 text-gray-600">
              Enable the plugin to display a name input screen when a new order is created.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">Flexible configuration options</h3>
            <p className="mt-4 text-gray-600">
              Set the system to prompt for an order name automatically or allow manual entry as needed.
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="relative bg-gradient-to-tr from-[#A8C7E0] to-[#4A90E2] py-24 text-center text-white">
        <div className="relative mx-auto px-4">
          <h2 className="text-4xl xl:text-6xl mb-6">Ready to enhance your order management?</h2>
          <p className="text-xl mb-10">
            Start using the <span className='font-semibold'>Named order</span> plugin for seamless order processing and management.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/4402022851602-Configure-named-order"
            className="button-rounded-white px-12 py-4"
            target="_blank" rel="noreferrer">
            Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default NamedOrderPlugin;
